import { useState } from 'react';
import { Typography, IconButton, CircularProgress, Box } from '@mui/material';
import { ChevronLeft, ChevronRight, CalendarToday } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import 'dayjs/locale/en';
import weekday from 'dayjs/plugin/weekday';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import updateLocale from 'dayjs/plugin/updateLocale';
import styles from './style.module.css';
import ModalMaintenanceTask from '../ModalMaintenanceTask';
import { ACTIONS } from '../../constants/actionTables';
import IntervalIndicator from '../IntervalIndicator';

dayjs.extend(weekday);
dayjs.extend(localizedFormat);
dayjs.extend(updateLocale);

// Configure dayjs to start week on Monday
dayjs.updateLocale('en', {
  weekStart: 1,
});
dayjs.updateLocale('de', {
  weekStart: 1,
});

function UpcomingMaintenanceCalendar({
  maintenanceTasks = [],
  fetchData,
  machineGroupName,
  machineGroupImg,
  isLoading = false,
}) {
  const { t, i18n } = useTranslation();
  const [currentDate, setCurrentDate] = useState(() => {
    const locale = i18n.language === 'de' ? 'de' : 'en';
    return dayjs().locale(locale);
  });

  // Set dayjs locale based on current language
  const currentLocale = i18n.language === 'de' ? 'de' : 'en';
  const localizedCurrentDate = currentDate.locale(currentLocale);

  // Get days in month starting from Monday
  const startOfMonth = localizedCurrentDate.startOf('month');
  const endOfMonth = localizedCurrentDate.endOf('month');
  // Start calendar from Monday - use startOf('week') which now starts on Monday
  const startCalendar = startOfMonth.startOf('week');
  const endCalendar = endOfMonth.endOf('week');

  const calendarDays = [];
  let day = startCalendar;

  while (day.isBefore(endCalendar) || day.isSame(endCalendar, 'day')) {
    calendarDays.push(day);
    day = day.add(1, 'day');
  }

  const handlePrevMonth = () => {
    setCurrentDate(currentDate.subtract(1, 'month').locale(currentLocale));
  };

  const handleNextMonth = () => {
    setCurrentDate(currentDate.add(1, 'month').locale(currentLocale));
  };

  // Get tasks for current month
  const monthTasks = (maintenanceTasks || []).filter((task) => {
    const taskDate = dayjs(task.created_at);
    return (
      taskDate.month() === localizedCurrentDate.month() &&
      taskDate.year() === localizedCurrentDate.year()
    );
  });

  // Group tasks by date
  const tasksByDate = monthTasks.reduce((acc, task) => {
    const dateKey = dayjs(task.created_at).format('YYYY-MM-DD');
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(task);
    return acc;
  }, {});

  const hasTaskOnDate = (date) => {
    return tasksByDate[date.format('YYYY-MM-DD')];
  };

  // Localized day names starting from Monday
  const getDayNames = () => {
    if (currentLocale === 'de') {
      return ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
    } else {
      return ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
    }
  };

  const dayNames = getDayNames();

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <CalendarToday fontSize="small" color="action" />
          <Typography variant="body2" fontWeight="600">
            {t('machineList.upcomingMaintenance')}
          </Typography>
        </div>
      </div>

      {/* Loading state */}
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          {/* Calendar */}
          <div className={styles.calendar}>
            {/* Month navigation */}
            <div className={styles.monthHeader}>
              <Typography variant="body1" fontWeight="600">
                {localizedCurrentDate.format('MMMM YYYY')}
              </Typography>
              <div className={styles.monthNav}>
                <IconButton size="small" onClick={handlePrevMonth}>
                  <ChevronLeft fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={handleNextMonth}>
                  <ChevronRight fontSize="small" />
                </IconButton>
              </div>
            </div>

            {/* Day names */}
            <div className={styles.dayNames}>
              {dayNames.map((name, index) => (
                <div key={index} className={styles.dayName}>
                  {name}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className={styles.calendarGrid}>
              {calendarDays.map((day, index) => {
                const isCurrentMonth = day.month() === localizedCurrentDate.month();
                const hasTask = hasTaskOnDate(day);
                const isToday = day.isSame(dayjs(), 'day');

                return (
                  <div
                    key={index}
                    className={`${styles.calendarDay} ${!isCurrentMonth ? styles.otherMonth : ''} ${
                      hasTask ? styles.hasTask : ''
                    } ${isToday ? styles.today : ''}`}
                  >
                    {day.date()}
                    {hasTask && <div className={styles.taskDot} />}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Task list */}
          <div className={styles.taskList}>
            {(maintenanceTasks || []).length > 0 ? (
              (maintenanceTasks || []).map((task, index) => {
                let intervalType, intervalValue;
                if (task.maintenance && task.maintenance.interval) {
                  try {
                    const interval = JSON.parse(task.maintenance.interval);
                    intervalType = interval.type;
                    intervalValue = interval.value;
                  } catch (e) {
                    console.warn('Failed to parse interval:', task.maintenance.interval);
                  }
                }

                return (
                  <div key={index} className={styles.taskWrapper}>
                    <ModalMaintenanceTask
                      task={task}
                      type={ACTIONS.PROFILE}
                      fetchdata={fetchData}
                      machineGroupName={machineGroupName}
                      machineGroupImg={machineGroupImg}
                    />
                    <IntervalIndicator
                      intervalType={intervalType}
                      intervalValue={intervalValue}
                      size="small"
                      position="absolute"
                    />
                  </div>
                );
              })
            ) : (
              <div className={styles.noTasks}>
                <Typography variant="body2" color="text.secondary">
                  {t('machineList.noMaintenance')}
                </Typography>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default UpcomingMaintenanceCalendar;
